import algoliasearch from 'algoliasearch'
import classnames from 'classnames'
import { Component } from 'preact'
import qs from 'qs'
import { Configure, Index, InstantSearch } from 'react-instantsearch-dom'
import Button from '../../control-components/ui-components/Button'
import Phrase from '../../libs/translationsUtils/phrase'
import { Credentials, getSearchToken, insights } from '../../services/algoliaService'
import globals from '../../utils/globals'
import { getFilters } from '../algolia/components/Search'
import Icon from '../assets'
import { common } from '../common'
import CustomCurrentRefinements from './FilterList'
import CustomHierarchicalMenu from './filters/HierarchicalMenu'
import SourceTabs from './filters/SourceTabs'
import SourceType from './filters/SourceType'
import TagsList from './filters/Tags'
import TopicType from './filters/TopicType'
import CustomSearchBox from './SearchBox'
import SearchResults from './SearchResults'

interface State {
  // @ts-expect-error
  client: algoliasearch | null
  enableLoader: boolean
  pageCount: number
  searchState: SearchState
  isFiltersOverlay: boolean
  isKnowledgeBaseActive: boolean
  totalAmount: {
    community: number
    zendesk: number
    salesforce: number
    skilljar: number
    freshdesk: number
    totalTopics: number
  }
  sourceTabs: {[key: string]: boolean}
  listIndexes: string[]
  isZendeskFederatedSearch: boolean
  hiddenCategories: number[]
}

interface Props {
  defaultCdn: string
  attachmentCdn: string
  isGuest: boolean
  isFederatedSalesforceSearch: boolean
  isFederatedSkilljarSearch: boolean
  isFederatedFreshdeskSearch: boolean
}

const createURL = (state: unknown) => `?${qs.stringify(state)}`

interface SearchState {
  query: string
  refinementList?: {
    tags: string[]
    content_type: string[]
    source: string[]
  }
  hierarchicalMenu?: {
    'categories.lvl0'?: string
    'kb.categories.lvl0'?: string
  }
}

interface StateInUrl {
  q?: string
  tags?: string[]
  content_type?: string[]
  source?: string[]|string
  category?: string
  kb_category?: string
  search_type?: 'tag'
}

const searchStateToUrl = (searchState: SearchState) => {
  const location = window.location
  if (!searchState) return ''
  const stateToStoreInUrl: StateInUrl = {}
  if (searchState.query) {
    stateToStoreInUrl.q = searchState.query
  }
  if (searchState.refinementList?.tags) {
    stateToStoreInUrl.tags = searchState.refinementList.tags
  }

  if (searchState.hierarchicalMenu?.['categories.lvl0']) {
    stateToStoreInUrl.category = searchState.hierarchicalMenu['categories.lvl0']
  }

  if (searchState.hierarchicalMenu?.['kb.categories.lvl0']) {
    stateToStoreInUrl.kb_category = searchState.hierarchicalMenu['kb.categories.lvl0']
  }

  if (searchState.refinementList?.content_type) {
    stateToStoreInUrl.content_type = searchState.refinementList.content_type
  }

  if (searchState.refinementList?.source) {
    stateToStoreInUrl.source = searchState.refinementList.source
  }

  return `${location.pathname}${createURL(stateToStoreInUrl)}`
}

function asArray(s: undefined|string|string[]): string[] {
  if (!s) return []
  return typeof s === 'string' ? [s] : s
}

const urlToSearchState = ({ search }: Location): SearchState => {
  const stateInUrl: StateInUrl = qs.parse(search.slice(1))

  if (stateInUrl.search_type === 'tag' && stateInUrl.q) {
    return {
      query: '',
      refinementList: {
        tags: [stateInUrl.q || ''],
        content_type: [],
        source: []
      }
    }
  }

  return {
    query: stateInUrl.q ?? '',
    refinementList: {
      tags: stateInUrl.tags ?? [],
      content_type: stateInUrl.content_type ? stateInUrl.content_type : [],
      source: asArray(stateInUrl.source)
    },
    hierarchicalMenu: {
      'categories.lvl0': stateInUrl.category ?? '',
      'kb.categories.lvl0': stateInUrl.kb_category ?? ''
    }
  }
}

class InstantSearchComponent extends Component<Props, State> {
  debouncedSetState: number = 0
  allIndexes: string[]
  constructor(props: Props) {
    super(props)
    const searchState = urlToSearchState(location)
    this.state = {
      client: null,
      enableLoader: false,
      pageCount: 0,
      searchState,
      isFiltersOverlay: false,
      totalAmount: {
        community: 0,
        zendesk: 0,
        salesforce: 0,
        skilljar: 0,
        freshdesk: 0,
        totalTopics: 0
      },
      isKnowledgeBaseActive: false,
      sourceTabs: searchState.refinementList?.source.reduce<State['sourceTabs']>((t, source) => {
        t[source] = true
        return t
      }, {}) || {},
      listIndexes: [],
      isZendeskFederatedSearch: false,
      hiddenCategories: []
    }
    this.handleStateChange = this.handleStateChange.bind(this)
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.totalTopicsAmount = this.totalTopicsAmount.bind(this)
    this.updateSources = this.updateSources.bind(this)
    this.resetSources = this.resetSources.bind(this)
    this.cb = this.cb.bind(this)
    this.isFederatedSearchEnabled = this.isFederatedSearchEnabled.bind(this)
    this.allIndexes = [
      `${this.communityId}`,
      `${this.communityId}-zendesk`,
      `${this.communityId}-salesforce`,
      `${this.communityId}-skilljar`,
      `${this.communityId}-freshdesk`
    ]
  }

  communityId = globals('communityId')

  async cb(response: Credentials) {
    const client = algoliasearch(response.client_id, response.token)
    const basicClient = algoliasearch(response.client_id, response.basicToken)

    this.setState({
      client: client,
      isZendeskFederatedSearch: response.isZendeskFederatedSearchEnabled,
      hiddenCategories: response.hiddenCategories
    })

    insights('init', {
      appId: response.client_id,
      apiKey: response.token
    })

    let indexes: { items: Array<{ name: string }> } = { items: [] }
    try {
      indexes = await basicClient.listIndexes()
    } catch (e) {
      this.setState({
        listIndexes: [
          `${this.communityId}`,
          `${this.communityId}-zendesk`,
          `${this.communityId}-salesforce`,
          `${this.communityId}-skilljar`,
          `${this.communityId}-freshdesk`
        ]
      })
      return
    }

    let indexNames: string[] = indexes.items.map((item) => item.name)

    if (!common.isModuleActive('federatedSearchApi')) {
      indexNames = indexNames.filter((i: string) => i !== `${this.communityId}-federated`)
    }

    this.setState({
      listIndexes: indexNames
    })
  }

  async componentDidMount() {
    await getSearchToken((c: Credentials) => {
      this.cb(c)
    })
  }

  handleStateChange(updatedSearchState: SearchState) {
    clearTimeout(this.debouncedSetState)

    const searchState = { ...this.state.searchState, ...updatedSearchState }

    this.debouncedSetState = window.setTimeout(() => {
      history.pushState(searchState, '', searchStateToUrl(this.state.searchState))
    }, 400)

    const pageChanged = searchStateToUrl(this.state.searchState) === searchStateToUrl(searchState)

    if (!pageChanged) {
      this.setState({ pageCount: 0 })
    }

    this.setState({
      searchState: searchState
    })
  }

  handleFilterChange() {
    this.setState({
      isFiltersOverlay: !this.state.isFiltersOverlay
    })

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  totalTopicsAmount(totalAmount: State['totalAmount']) {
    if (JSON.stringify(this.state.totalAmount) !== JSON.stringify(totalAmount)) {
      this.setState({
        totalAmount: totalAmount
      })
    }
  }

  updateSources(source: string, enabled: boolean) {
    const newState = this.state.searchState
    if (!newState.refinementList) return
    const sourceTabs = {
      ...this.state.sourceTabs,
      [source]: enabled
    }
    newState.refinementList.source = Object.keys(sourceTabs).filter(s => sourceTabs[s])

    this.setState({ searchState: newState, sourceTabs: sourceTabs })
  }

  resetSources() {
    this.setState({
      sourceTabs: {}
    })
  }

  isFederatedSearchEnabled() {
    return this.state.listIndexes.includes(`${this.communityId}-federated`)
  }

  render() {
    const {
      defaultCdn,
      attachmentCdn,
      isGuest,
      isFederatedSalesforceSearch,
      isFederatedSkilljarSearch,
      isFederatedFreshdeskSearch
    } = this.props
    const {
      client,
      enableLoader,
      isFiltersOverlay,
      pageCount,
      totalAmount,
      isKnowledgeBaseActive,
      sourceTabs,
      listIndexes,
      isZendeskFederatedSearch,
      hiddenCategories
    } = this.state

    if (!client) {
      return null
    }

    const enabledSources = Object.keys(sourceTabs)
      .filter((key) => sourceTabs[key])
    const sourceFilters = enabledSources.map(
      (enabledSource) => `source: ${enabledSource}`
    )

    const externalSourcesEnabled =
      isZendeskFederatedSearch ||
      isFederatedSalesforceSearch ||
      isFederatedSkilljarSearch ||
      isFederatedFreshdeskSearch ||
      this.isFederatedSearchEnabled()

    return (
      <div className='instant-search-wrapper'>
        <InstantSearch
          searchClient={this.state.client}
          indexName={this.communityId}
          searchState={this.state.searchState}
          onSearchStateChange={this.handleStateChange}
        >
          {listIndexes.includes(this.communityId) && (
            <Index indexName={this.communityId}>
              <Configure
                hitsPerPage={externalSourcesEnabled ? 20 : 25}
                facetingAfterDistinct
                page={pageCount}
                facetFilters={sourceFilters}
                restrictSearchableAttributes={[
                  'title',
                  'first_post',
                  'answer',
                  'reply',
                  'tags',
                  'forum_title',
                  'category_title'
                ]}
                analyticsTags={['destination', 'instant-search']}
                clickAnalytics
                filters={getFilters({ hiddenCategories: hiddenCategories })}
              />
            </Index>
          )}
          {isZendeskFederatedSearch && listIndexes.includes(`${this.communityId}-zendesk`) && (
            <Index indexName={`${this.communityId}-zendesk`}>
              <Configure
                hitsPerPage={
                  enabledSources.length === 1 && enabledSources[0] === 'zendesk' ? 25 : 5
                }
                facetFilters={sourceFilters}
                page={pageCount}
                analyticsTags={['destination', 'instant-search']}
                clickAnalytics
              />
            </Index>
          )}
          {isFederatedSalesforceSearch && listIndexes.includes(`${this.communityId}-salesforce`) && (
            <Index indexName={`${this.communityId}-salesforce`}>
              <Configure
                hitsPerPage={
                  enabledSources.length === 1 && enabledSources[0] === 'salesforce' ? 25 : 5
                }
                facetFilters={sourceFilters}
                page={pageCount}
                analyticsTags={['destination', 'instant-search']}
                clickAnalytics
              />
            </Index>
          )}
          {isFederatedSkilljarSearch && listIndexes.includes(`${this.communityId}-skilljar`) && (
            <Index indexName={`${this.communityId}-skilljar`}>
              <Configure
                hitsPerPage={
                  enabledSources.length === 1 && enabledSources[0] === 'skilljar' ? 25 : 5
                }
                facetFilters={sourceFilters}
                page={pageCount}
                analyticsTags={['destination', 'instant-search']}
                clickAnalytics
              />
            </Index>
          )}
          {isFederatedFreshdeskSearch && listIndexes.includes(`${this.communityId}-freshdesk`) && (
            <Index indexName={`${this.communityId}-freshdesk`}>
              <Configure
                hitsPerPage={
                  enabledSources.length === 1 && enabledSources[0] === 'freshdesk' ? 25 : 5
                }
                facetFilters={sourceFilters}
                page={pageCount}
                analyticsTags={['destination', 'instant-search']}
                clickAnalytics
              />
            </Index>
          )}

          {this.isFederatedSearchEnabled() && (
            <Index indexName={`${this.communityId}-federated`}>
              <Configure
                hitsPerPage={
                  enabledSources.length === 1 && enabledSources[0] === 'federated' ? 25 : 5
                }
                facetFilters={sourceFilters}
                page={pageCount}
                analyticsTags={['destination', 'instant-search']}
                clickAnalytics
              />
            </Index>
          )}
          <div className='instant-search-input-wrapper'>
            <CustomSearchBox
              defaultCdn={defaultCdn}
              enableLoader={enableLoader}
              loaderCallback={() => this.setState({ enableLoader: true })}
            />
          </div>
          <button className='instant-search__filter' onClick={this.handleFilterChange}>
            <Icon width={18} height={12} color='#6B707E' icon='filter' />
          </button>
          <div className='instant-search__results-wrapper'>
            <div
              className={`instant-search__results-wrapper-filter ${
                !isFiltersOverlay ? 'instant-search__results-wrapper-filter--hide' : ''
              }`}
            >
              <div className='instant-search__results-wrapper-filter-header'>
                <Icon icon='close' onClick={this.handleFilterChange} />
                <h3 className='instant-search__results-wrapper-filter-header-heading'>
                  <Phrase p='advanced.search.filters' />
                </h3>
                <button className='instant-search__results-wrapper-filter-header-btn'>
                  <Phrase m='Common' p='advanced.search.filter.clear.all' />
                </button>
              </div>
              <div className='instant-search__results-wrapper-filters'>
                {isKnowledgeBaseActive && !externalSourcesEnabled && (
                  <SourceType
                    title={<Phrase m='Common' p='advanced.search.filter.source' />}
                    attribute='source'
                    searchable
                  />
                )}
                {externalSourcesEnabled && (
                  <div>
                    <SourceTabs
                      title={<Phrase m='Common' p='advanced.search.filter.source' />}
                      updateSourceTabs={this.updateSources}
                      tabs={sourceTabs}
                    />
                    <div className='is-hidden'>
                      <SourceType
                        title={<Phrase m='Common' p='advanced.search.filter.source' />}
                        attribute='source'
                        operator='and'
                        searchable
                      />
                    </div>
                  </div>
                )}

                <TopicType
                  title={<Phrase m='Common' p='advanced.search.filter.topic.type' />}
                  attribute='content_type'
                  searchable
                />

                <CustomHierarchicalMenu
                  title={<Phrase m='Common' p='advanced.search.filter.community.category' />}
                  attributes={['categories.lvl0', 'categories.lvl1']}
                />

                {isKnowledgeBaseActive && (
                  <CustomHierarchicalMenu
                    title={<Phrase m='Common' p='advanced.search.filter.knowledgebase.category' />}
                    attributes={['kb.categories.lvl0', 'kb.categories.lvl1']}
                  />
                )}

                <TagsList
                  title={<Phrase m='Common' p='advanced.search.filter.tag' />}
                  operator='and'
                  attribute='tags'
                  searchable
                  limit={10}
                />
              </div>
              {isFiltersOverlay && (
                <div className='instant-search__results-wrapper-filter-show-more-wrapper'>
                  <Button
                    cta
                    className='instant-search__results-wrapper-filter-show-more'
                    onClick={this.handleFilterChange}
                  >
                    <Phrase
                      m='Forum'
                      p='advanced.search.show.more.topics'
                      count={{ number: totalAmount.totalTopics.toString() }}
                    />
                  </Button>
                </div>
              )}
            </div>

            <div className={classnames('col col--main has--side search-topic-list-wrapper')}>
              <CustomCurrentRefinements resetSources={this.resetSources} />

              <SearchResults
                isGuest={isGuest}
                communityId={this.communityId}
                attachmentCdn={attachmentCdn}
                defaultCdn={defaultCdn}
                enableLoader={enableLoader}
                isZendeskFederatedSearch={isZendeskFederatedSearch}
                isFederatedSalesforceSearch={isFederatedSalesforceSearch}
                isFederatedSkilljarSearch={isFederatedSkilljarSearch}
                isFederatedFreshdeskSearch={isFederatedFreshdeskSearch}
                isFederatedSearch={this.isFederatedSearchEnabled()}
                pageCount={pageCount}
                loadNextPage={() => this.setState({ pageCount: pageCount + 1 })}
                loaderCallback={(enableLoader: boolean) => {
                  return this.setState({ enableLoader: enableLoader })
                }}
                totalTopicsAmount={this.totalTopicsAmount}
              />
            </div>
          </div>
        </InstantSearch>
      </div>
    )
  }
}

export default InstantSearchComponent
